// API wrappers for the Media library. Every image the client uploads lands
// here: uploads go through POST /tenant/media as portal-resized display +
// thumbnail variants (see image-resize.ts) and are registered as one library
// item, reusable anywhere the portal offers an image.
import { formatBytes } from "@pgs-core/portal-lib/format";
import type { ApiClient } from "./api";
import { prepareGalleryImage } from "./image-resize";

export interface MediaItem {
  id: string;
  key: string;
  thumbKey: string | null;
  title: string;
  caption: string;
  alt: string;
  fileName: string;
  mimeType: string;
  size: number;
  width: number | null;
  height: number | null;
  createdAt: string;
  /** Transient preview URLs from the upload response; not persisted. */
  url?: string | null;
  thumbUrl?: string | null;
}

export interface MediaResponse {
  media: MediaItem[];
  /** Public R2 base URL for displaying stored files; null when unresolved. */
  assetsBaseUrl: string | null;
  /** Total matching items (equals media.length when not paginated). */
  total: number;
}

export interface MediaUsageEntry {
  type: "gallery" | "page" | "logo" | "favicon" | "menu_item";
  id?: string;
  label: string;
}

/** `query` is an optional list-controls query string (page/per_page/q/sort). */
export async function fetchMedia(api: ApiClient, query = ""): Promise<MediaResponse> {
  const data = await api.request<{ media?: MediaItem[]; assets_base_url?: string | null; total?: number }>(
    `/tenant/media${query ? `?${query}` : ""}`
  );
  const media = data.media ?? [];
  return { media, assetsBaseUrl: data.assets_base_url ?? null, total: data.total ?? media.length };
}

/** Resolve a stored file's display URL: transient upload URL first, then the key. */
export function mediaUrl(
  item: Pick<MediaItem, "key" | "thumbKey" | "url" | "thumbUrl">,
  assetsBaseUrl: string | null,
  { thumb = false } = {}
): string | null {
  const transient = thumb ? (item.thumbUrl ?? item.url) : item.url;
  if (transient) return transient;
  const key = thumb ? (item.thumbKey ?? item.key) : item.key;
  return assetsBaseUrl && key ? `${assetsBaseUrl}/${key}` : null;
}

/**
 * Full upload pipeline for one library file: resize in the browser (display +
 * thumbnail variants), then upload and register in one request. Returns the
 * new item with transient preview URLs attached.
 */
export async function uploadMedia(api: ApiClient, file: File): Promise<MediaItem> {
  const prepared = await prepareGalleryImage(file);

  const form = new FormData();
  form.append("file", prepared.displayFile);
  if (prepared.thumbFile) form.append("thumb", prepared.thumbFile);
  form.append("title", "");
  if (prepared.width) form.append("width", String(prepared.width));
  if (prepared.height) form.append("height", String(prepared.height));

  return api.upload<MediaItem>("/tenant/media", form);
}

export function updateMedia(
  api: ApiClient,
  mediaId: string,
  fields: { title: string; caption: string; alt: string }
): Promise<unknown> {
  return api.request(`/tenant/media/${mediaId}`, {
    method: "PUT",
    body: JSON.stringify(fields)
  });
}

export function fetchMediaUsage(
  api: ApiClient,
  mediaId: string
): Promise<{ id: string; usage: MediaUsageEntry[] }> {
  return api.request(`/tenant/media/${mediaId}/usage`);
}

export function deleteMedia(api: ApiClient, mediaId: string): Promise<unknown> {
  return api.request(`/tenant/media/${mediaId}`, { method: "DELETE" });
}

/** The display name for an item: its title, or the original filename greyed. */
export function mediaDisplayName(item: Pick<MediaItem, "title" | "fileName" | "key">): string {
  return item.title || item.fileName || item.key.split("/").pop() || item.key;
}

export function isImageMedia(item: Pick<MediaItem, "mimeType" | "key">): boolean {
  if (item.mimeType) return item.mimeType.startsWith("image/");
  return !/\.pdf$/i.test(item.key);
}

export function formatFileSize(bytes: number): string {
  return bytes > 0 ? formatBytes(bytes) : "—";
}

/** File-type label from the stored key's extension, e.g. "WEBP". */
export function fileTypeLabel(item: Pick<MediaItem, "key">): string {
  return item.key.split(".").pop()?.toUpperCase() ?? "";
}
